import * as THREE from "three";
import { Polygon, Vector3 } from "opengeometry";
import { Glyphs } from "@opengeometry/openglyph";

import { ElementType } from "../elements/base-type";
import { OPElement, IPlaceable } from "../elements/op-element";
import { IShape } from "../shapes/base-type";
import { Event } from "../../../../utils/event";
import type { Vec3 } from "../types";
import {
  DatumOptionsBase,
  DatumType,
  DATUM_COLORS,
} from "./base-type";
import { applyVisibility, setProfileView, setModelView } from "./internals/visibility";
import { disposeSubElements } from "./internals/lifecycle";

const LABEL_FONT_SIZE = 0.3;
const LABEL_OFFSET = 0.15;

export interface ReferencePlaneOptions extends DatumOptionsBase {
  datumType: DatumType.REFERENCE_PLANE;
  /** World-space origin of the plane. */
  origin: [number, number, number];
  /** Plane normal (will be normalised). */
  normal: [number, number, number];
  /** Half-width along the plane's local X and Y axes. */
  extents: [number, number];
  /** Whether the plane body is drawn as a semi-transparent polygon. */
  showBody: boolean;
}

/**
 * Free-angle guide plane. Rendered as a semi-transparent polygon with
 * an outline and a label at one corner. Used for constraining rotations,
 * aligning elements, or setting up work planes for sketches.
 */
export class ReferencePlane extends OPElement implements IShape, IPlaceable {
  ogid: string = crypto.randomUUID();
  ogType: string = ElementType.DATUM_REFERENCE_PLANE;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  subElements3D: Map<string, THREE.Object3D> = new Map();

  selected = false;
  edit = false;
  locked = false;

  onDatumUpdated: Event<null> = new Event();

  private _profileView = true;
  private _modelView = true;

  propertySet: ReferencePlaneOptions = {
    labelName: "Reference Plane",
    datumType: DatumType.REFERENCE_PLANE,
    color: DATUM_COLORS.REFERENCE,
    lineWidth: 1,
    visibility: { plan: true, model: true },
    origin: [0, 0, 0],
    normal: [0, 1, 0],
    extents: [3, 3],
    showBody: true,
  };

  constructor(config?: Partial<ReferencePlaneOptions>) {
    super();
    if (config) {
      this.propertySet = {
        ...this.propertySet,
        ...config,
        visibility: {
          ...this.propertySet.visibility,
          ...(config.visibility ?? {}),
        },
      };
    }
    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
    const resolved = applyVisibility(
      this.subElements2D,
      this.subElements3D,
      this.propertySet.visibility,
    );
    this._profileView = resolved.plan;
    this._modelView = resolved.model;
  }

  move(delta: Vec3): void {
    const [ox, oy, oz] = this.propertySet.origin;
    this.propertySet.origin = [ox + delta[0], oy + delta[1], oz + delta[2]];
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  rotate(_angleDeg: number, _axis?: Vec3, _anchor?: Vec3): void {
    console.warn(
      "ReferencePlane.rotate not implemented; mutate `normal` via setOPConfig instead.",
    );
  }

  get labelName(): string {
    return this.propertySet.labelName;
  }
  set labelName(value: string) {
    this.propertySet.labelName = value;
    this.setOPGeometry();
  }

  get datumType(): DatumType {
    return this.propertySet.datumType;
  }

  get profileView(): boolean {
    return this._profileView;
  }
  set profileView(value: boolean) {
    this._profileView = value;
    setProfileView(this.subElements2D, value);
  }

  get modelView(): boolean {
    return this._modelView;
  }
  set modelView(value: boolean) {
    this._modelView = value;
    setModelView(this.subElements3D, value);
  }

  setOPConfig(config: Partial<ReferencePlaneOptions>): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    this.propertySet = {
      ...this.propertySet,
      ...config,
      visibility: {
        ...this.propertySet.visibility,
        ...(config.visibility ?? {}),
      },
    };
    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): ReferencePlaneOptions {
    return this.propertySet;
  }

  setOPMaterial(): void {
    // Intentionally empty — geometry rebuilds handle every visual update.
  }

  setOPGeometry(): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    const { origin, normal, extents, color, showBody } = this.propertySet;
    const drawColor = color ?? DATUM_COLORS.REFERENCE;

    const nrm = new Vector3(normal[0], normal[1], normal[2]);
    const nlen = Math.sqrt(nrm.x * nrm.x + nrm.y * nrm.y + nrm.z * nrm.z) || 1;
    const n = new Vector3(nrm.x / nlen, nrm.y / nlen, nrm.z / nlen);

    // Build an orthonormal basis in the plane.
    const worldUp = Math.abs(n.y) < 0.99 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
    const u = this.crossV(worldUp, n);
    const uLen = Math.sqrt(u.x * u.x + u.y * u.y + u.z * u.z) || 1;
    const uN = new Vector3(u.x / uLen, u.y / uLen, u.z / uLen);
    const v = this.crossV(n, uN);

    const o = new Vector3(origin[0], origin[1], origin[2]);
    const [ex, ey] = extents;

    const corners = [
      this.addV3(o, this.scaleV(uN, -ex), this.scaleV(v, -ey)),
      this.addV3(o, this.scaleV(uN, ex), this.scaleV(v, -ey)),
      this.addV3(o, this.scaleV(uN, ex), this.scaleV(v, ey)),
      this.addV3(o, this.scaleV(uN, -ex), this.scaleV(v, ey)),
    ];

    if (showBody) {
      const plane = new Polygon({ vertices: corners, color: drawColor });
      plane.outline = true;
      plane.outlineColor = drawColor;
      const mat = plane.material as THREE.MeshBasicMaterial;
      mat.transparent = true;
      mat.opacity = 0.08;
      mat.side = THREE.DoubleSide;
      mat.depthWrite = false;
      this.add(plane);
      this.subElements3D.set("body", plane);
    }

    // Label at corners[3] with a small offset so it sits just outside the plane edge.
    const labelCorner = corners[3];
    const label = Glyphs.addGlyph(this.propertySet.labelName, LABEL_FONT_SIZE, drawColor, true);
    label.textMesh.geometry.computeBoundingBox();
    const bbox = label.textMesh.geometry.boundingBox!;
    const halfWidth = (bbox.max.x - bbox.min.x) * 0.5;
    const halfDepth = (bbox.max.y - bbox.min.y) * 0.5;
    label.position.set(
      labelCorner.x - halfWidth,
      labelCorner.y,
      labelCorner.z - halfDepth + LABEL_OFFSET,
    );
    this.add(label);
    this.subElements3D.set("label", label);

    this.onDatumUpdated.trigger(null);
  }

  private crossV(a: Vector3, b: Vector3): Vector3 {
    return new Vector3(
      a.y * b.z - a.z * b.y,
      a.z * b.x - a.x * b.z,
      a.x * b.y - a.y * b.x,
    );
  }

  private scaleV(a: Vector3, s: number): Vector3 {
    return new Vector3(a.x * s, a.y * s, a.z * s);
  }

  private addV3(a: Vector3, b: Vector3, c: Vector3): Vector3 {
    return new Vector3(a.x + b.x + c.x, a.y + b.y + c.y, a.z + b.z + c.z);
  }
}
